import { Flex, Color, Text } from "src/elements";
import { useRef, type KeyboardEvent } from "react";
import { Transition } from "src/utils";
import styled from "styled-components";
import { colorToRGBA } from "src/utils/colorToRgba";
import { useHoverClickable } from "src/utils/hoverClickable";

const sizeDefault = "14px";
const sizeSmall = "10px";

const RadioContainer = styled(Flex)`
  gap: 5px;
  align-items: center;
`;

const RadioOuterCircle = styled(Flex)<{ size?: "default" | "small" }>`
  min-width: ${({ size }) => (size === "small" ? sizeSmall : sizeDefault)};
  min-height: ${({ size }) => (size === "small" ? sizeSmall : sizeDefault)};
  width: ${({ size }) => (size === "small" ? sizeSmall : sizeDefault)};
  height: ${({ size }) => (size === "small" ? sizeSmall : sizeDefault)};
  border-style: solid;
  border-width: 1px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
`;

const RadioInnerCircle = styled(Flex)<{ isVisible: boolean }>`
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  border-radius: 50%;
  transform: ${({ isVisible }) => `scale(${isVisible ? "100%" : "0%"})`};
  transform-origin: center;
  transition: transform ${Transition.fast};
`;

interface Props<T> {
  label?: string;
  value: T;
  setIsChecked: (value: T) => void;
  size?: "default" | "small";
  isChecked?: boolean;
  isDisabled?: boolean;
  isUnselectable?: boolean;
  variant?: "body2" | "small2";
  /** Set by `Radios` for its single tab stop; a lone radio is a tab stop of its own. */
  tabIndex?: number;
  /** Set by `Radios` to move between its options with the arrow keys. */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  /** Set by `Radios` so the arrow keys can move focus here. */
  refOption?: (element: HTMLDivElement | null) => void;
}

export const Radio = <T,>({
  label,
  value,
  size,
  variant,
  isDisabled,
  isUnselectable,
  isChecked,
  setIsChecked,
  tabIndex,
  onKeyDown,
  refOption,
}: Props<T>) => {
  const refRadio = useRef<HTMLDivElement>(null);
  const { isHovered, isActive } = useHoverClickable(refRadio);

  const radioColor =
    (isDisabled && Color.textDisabled) ||
    (isActive && Color.primaryActive) ||
    (isChecked && Color.primary) ||
    (isHovered && colorToRGBA(Color.primary, 0.5)) ||
    Color.spaceGray;

  const textColor =
    (isDisabled && Color.textDisabled) || (isChecked && Color.textMain) || Color.textSecondary;

  const textVariant = size === "small" ? "caption1" : "body2";

  const onClick = () => {
    if (isChecked && isUnselectable) {
      return setIsChecked(null as T);
    }

    if (!isChecked) {
      return setIsChecked(value);
    }
  };

  // Space and Enter pick the radio as a click does; other keys go to `Radios`.
  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      if (!isDisabled) onClick();
      return;
    }
    onKeyDown?.(event);
  };

  return (
    <RadioContainer
      ref={(element: HTMLDivElement | null) => {
        refRadio.current = element;
        refOption?.(element);
      }}
      role="radio"
      aria-checked={!!isChecked}
      aria-disabled={!!isDisabled}
      aria-label={label}
      tabIndex={tabIndex ?? (isDisabled ? -1 : 0)}
      onClick={onClick}
      onKeyDown={onKey}
      isDisabled={isDisabled}
    >
      <RadioOuterCircle size={size} borderColor={radioColor}>
        <RadioInnerCircle
          background={radioColor}
          isVisible={isChecked || (!isDisabled && isHovered)}
        />
      </RadioOuterCircle>

      {label && (
        <Text variant={variant || textVariant} color={textColor}>
          {label}
        </Text>
      )}
    </RadioContainer>
  );
};
